import React from 'react';
import styles from './ui.module.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'success' | 'danger' | 'warning';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Legacy hook from the page-band design; kept so callers stay valid. */
  onBand?: boolean;
  /** Square icon-only button — pass a single icon as the child. */
  iconOnly?: boolean;
  icon?: React.ReactNode;
}

const VARIANT: Record<ButtonVariant, string> = {
  primary: styles.btnPrimary,
  secondary: styles.btnSecondary,
  ghost: styles.btnGhost,
  success: styles.btnSuccess,
  danger: styles.btnDanger,
  warning: styles.btnWarning,
};

const SIZE: Record<ButtonSize, string> = {
  sm: styles.btnSm,
  md: '',
  lg: styles.btnLg,
};

/**
 * The only button in the app. Primary is navy and there is at most one per
 * view; success/danger/warning are reserved for what they say, never for
 * emphasis.
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
  variant = 'secondary',
  size = 'md',
  onBand = false,
  iconOnly = false,
  icon,
  children,
  className = '',
  type = 'button',
  ...rest
}, ref) => (
  <button
    ref={ref}
    type={type}
    className={[
      iconOnly ? styles.iconBtn : styles.btn,
      // An icon-only button is always outlined; `danger` still marks it destructive.
      iconOnly ? (variant === 'danger' ? styles.iconBtnDanger : '') : VARIANT[variant],
      iconOnly ? '' : SIZE[size],
      onBand ? styles.btnOnBand : '',
      className,
    ]
      .filter(Boolean)
      .join(' ')}
    {...rest}
  >
    {icon}
    {children}
  </button>
));

Button.displayName = 'Button';

export default Button;
